import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import {
  getAddons,
  addAddons,
  editAddons,
  deleteAddons,
} from "../../services/api/api";
import Loader from "../loader/Loader";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toolbar } from "primereact/toolbar";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { ToggleButton } from "primereact/togglebutton";
import { Sidebar } from "primereact/sidebar";
import { Divider } from "primereact/divider";
import {
  fetchData,
  showToast,
  removeZeroFront,
  formateName,
  formatToTitleCase,
  exportCustomCSV,
  exportCustomXLSX,
  trimObjectValues,
  getSerialNo,
} from "../utils/Function/Helper";
import {
  ExportButton,
  NewButton,
  DeleteButton,
  ActionButton,
  SearchBar,
  NormalButton,
} from "../utils/Atoms/Atoms";

const AddonsList = ({ type }) => {
  const source = type;
  const source_title = formatToTitleCase(source);
  const user = useSelector((state) => state.user);

  let emptyData = {
    superadmin_id: user?.userDetails?.ID || null,
    id: null,
    source: source,
    source_value: "",
    color: "#000000",
    text: "white",
    trigger_status: 0,
    position: "",
  };

  const [data, setData] = useState(emptyData);
  const [datas, setDatas] = useState([]);
  const [errors, setErrors] = useState({});
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedDatas, setSelectedDatas] = useState(null);
  const [popup, setPopup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingapi, setLoadingapi] = useState(true);
  const [buffering, setBuffering] = useState(false);
  const dt = useRef(null);

  // loader
  const loader = () => {
    setBuffering(true);
    setTimeout(() => {
      setBuffering(false);
    }, 500); // 0.5 second delay
  };

  // get datas
  useEffect(() => {
    setDatas([]);
    if (emptyData.superadmin_id) {
      fetchData(() => getAddons(emptyData), setDatas);
    }
    loader();
  }, [user, source, loadingapi]);

  // open add popup
  const openDialog = () => {
    emptyData.position = (
      1 + +(datas.length > 0 ? datas[datas.length - 1].position : 0)
    ).toString();
    setData(emptyData);
    setErrors({});
    setPopup(1);
  };

  // hide popup
  const hideDialog = () => {
    setData(emptyData);
    setPopup(null);
  };

  // input
  const onInputChange = (e, name) => {
    let _data = { ...data };
    let val;
    switch (name) {
      case "booking":
      case "finish":
      case "text":
      case "decline": {
        // Map name to corresponding trigger value
        const triggerValueMap = {
          booking: 1,
          finish: 2,
          decline: 3,
        };

        val =
          name === "text"
            ? e.value
              ? "black"
              : "white"
            : e.checked
            ? triggerValueMap[name]
            : 0;

        // Check if another status already has the same trigger status
        const hasTrigger = datas.some(
          (obj) =>
            obj.id !== _data.id && obj.trigger_status === triggerValueMap[name]
        );

        val !== 0 && hasTrigger
          ? showToast(
              "warning",
              `${
                name.charAt(0).toUpperCase() + name.slice(1)
              } Trigger Already Set!`,
              "top-center"
            )
          : name === "text"
          ? (_data["text"] = val)
          : (_data["trigger_status"] = Number(val));
        break;
      }

      case "color": {
        val = (e.target && e.target.value) || "";
        _data[name] = val.trim();
        setErrors({ ...errors, [name]: "" });
        break;
      }

      case "position": {
        val = (e.target && e.target.value) || "";
        if (parseInt(val)) {
          let num = val.replace(/[^0-9]/g, "").substring(0, 2);
          let alp = val
            .replace(/[^a-zA-Z]/g, "")
            .substring(0, 1)
            .toUpperCase();
          num = removeZeroFront(num);
          _data[name] = num + alp;
        } else {
          _data[name] = "";
        }
        setErrors({ ...errors, [name]: "" });
        break;
      }

      default: {
        val = (e.target && e.target.value).trimStart() || "";
        val = val.replace(/[^a-zA-Z0-9 ]/g, "");
        val = formateName(val);
        _data[name] = val;
        setErrors({ ...errors, [name]: "" });
        break;
      }
    }
    setData(_data);
  };

  // reseting the page
  const resetPage = () => {
    hideDialog();
    setLoadingapi((prev) => !prev);
  };

  // reset state after operation
  const resetStates = () => {
    setLoading(false);
    resetPage();
  };

  // handle Api Response
  const handleApiResponse = (response) => {
    response.status
      ? showToast("success", response.message)
      : showToast("warning", response.message);
    resetStates();
  };

  // validate all fields
  const validateField = (finalData) => {
    const newErrors = {};

    // source value validation
    if (!finalData.source_value) {
      newErrors.source_value = `${source_title} is required`;
    } else {
      const isSourceValueExist = datas.some(
        (item) =>
          item.id !== finalData.id &&
          item.source_value.toLowerCase() ===
            finalData.source_value.toLowerCase()
      );
      if (isSourceValueExist) {
        showToast("warning", `${source_title} Already Exist !`, "top-center");
        newErrors.source_value = `${source_title} already exist`;
      }
    }

    // position validation
    if (!finalData.position) {
      newErrors.position = `Position is required`;
    } else {
      const isPositionExist = datas.some(
        (item) =>
          item.id !== finalData.id &&
          item.position.toLowerCase() === finalData.position.toLowerCase()
      );
      if (isPositionExist) {
        showToast("warning", `Position Already Exist !`, "top-center");
        newErrors.position = `Position already exist`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // Returns true if no errors
  };

  // add and edit Item
  const addItem = async () => {
    let _data = trimObjectValues(data);
    // validation
    if (validateField(_data)) {
      // Perform API call
      setLoading(true);
      const response = data.id
        ? await editAddons(_data)
        : await addAddons(_data);
      handleApiResponse(response);
      setLoading(false);
    }
  };

  // export file
  const exportData = (type) => {
    let exportData = dt.current.props.value;
    let headers;
    let modifiedData = [];
    exportData.forEach((row, index) => {
      modifiedData.push({
        "s.no": index + 1,
        ...row,
      });
    });
    switch (source) {
      case "priority":
        headers = [
          "s.no",
          "source",
          "source_value",
          "color",
          "text",
          "position",
        ];
        break;

      case "lead-status":
        headers = [
          "s.no",
          "source",
          "source_value",
          "trigger_status",
          "position",
        ];
        break;

      default:
        headers = ["s.no", "source", "source_value", "position"];
        break;
    }
    if (type === "csv") {
      exportCustomCSV(headers, modifiedData, source);
    } else if (type === "xlsx") {
      exportCustomXLSX(headers, modifiedData, source);
    }
  };

  // save and cancel button
  const dataDialogFooter = (
    <React.Fragment>
      <NormalButton
        label="Cancel"
        icon="pi-times"
        outlined={true}
        onClick={hideDialog}
      />
      <NormalButton
        label="Save"
        icon="pi-check"
        disabled={loading}
        onClick={addItem}
      />
    </React.Fragment>
  );

  // add and delete button
  const rightToolbarTemplate = () => {
    return (
      <div className="flex flex-wrap gap-2">
        <SearchBar
          position={"left"}
          onInput={(e) => setGlobalFilter(e.target.value)}
        />
        <NewButton onClick={openDialog} />
        <DeleteButton
          onClick={() => setPopup(3)}
          disabled={!selectedDatas || !selectedDatas.length}
        />
      </div>
    );
  };

  // export button
  const leftToolbarTemplate = () => {
    return (
      <div className="flex flex-wrap gap-2">
        <ExportButton
          label={".csv"}
          datas={datas}
          onClick={() => exportData("csv")}
        />
        <ExportButton
          label={".xlsx"}
          datas={datas}
          onClick={() => exportData("xlsx")}
        />
      </div>
    );
  };

  // table header
  const header = (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
      <h3 className="m-0 hcolor">{source_title}</h3>
      <SearchBar
        position={"left"}
        onInput={(e) => setGlobalFilter(e.target.value)}
      />
    </div>
  );

  // action buttons
  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex">
        <ActionButton
          icon={"pi-pen-to-square"}
          onClick={() => {
            setData({ ...rowData });
            setErrors({});
            setPopup(1);
          }}
          id={"editTooltip"}
          toolText={"Edit"}
        />
        <ActionButton
          icon={"pi-trash"}
          onClick={() => {
            setData({ ...rowData });
            setPopup(2);
          }}
          id={"deleteTooltip"}
          toolText={"Delete"}
        />
      </div>
    );
  };

  // delete one
  const deleteData = async () => {
    setLoading(true);
    const response = await deleteAddons(data);
    if (response.status) {
      showToast("success", response.message);
      resetPage();
    } else {
      showToast("warning", response?.message || response);
    }
    setLoading(false);
  };

  // delete many
  const deleteSelectedDatas = () => {
    setLoading(true);
    const deletePromises = selectedDatas.map(async (item) => {
      const response = await deleteAddons(item);
      if (!response?.status) {
        showToast("warning", response?.message || response);
        return;
      }
    });
    Promise.all(deletePromises).then(() => {
      showToast("success", `${source_title}s Deleted`);
      setSelectedDatas(null);
      resetPage();
    });
    setLoading(false);
  };

  // delete footer buttons
  const deleteDialogFooter = (callTo) => {
    return (
      <>
        <React.Fragment>
          <NormalButton
            label="No"
            icon="pi-times"
            outlined={true}
            onClick={hideDialog}
          />
          <NormalButton
            label="Yes"
            icon="pi-check"
            disabled={loading}
            onClick={callTo}
          />
        </React.Fragment>
      </>
    );
  };

  // Tiggering Tag
  const tiggerTag = (rowData, trigger) => {
    let status = "No";
    let flag = "danger";

    switch (trigger) {
      case "book":
        if (rowData.trigger_status == 1) {
          status = "Tigger";
          flag = "success";
        }
        break;

      case "finish":
        if (rowData.trigger_status == 2) {
          status = "Tigger";
          flag = "success";
        }
        break;

      case "decline":
        if (rowData.trigger_status == 3) {
          status = "Tigger";
          flag = "success";
        }
        break;

      default:
        status = "NO";
        flag = "danger";
        break;
    }
    return <Tag value={status} severity={flag} />;
  };

  // Tag
  const setTag = (rowData) => (
    <Tag
      value={rowData.source_value}
      style={{ background: rowData.color, color: rowData.text }}
    />
  );

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  return (
    <>
      <ToastContainer />
      {buffering ? (
        <Loader />
      ) : (
        <div className="card">
          <Toolbar
            className="mb-4"
            start={leftToolbarTemplate}
            end={rightToolbarTemplate}
          ></Toolbar>

          {/* table */}
          <DataTable
            ref={dt}
            value={datas}
            paginator
            rows={10}
            className="datatable"
            rowsPerPageOptions={[5, 10, 15]}
            globalFilter={globalFilter}
            header={header}
            selection={selectedDatas}
            onSelectionChange={(e) => setSelectedDatas(e.value)}
            selectionMode="checkbox"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} datas"
          >
            <Column
              selectionMode="multiple"
              exportable={false}
              style={{ width: "30px" }}
            />
            <Column
              header="S.No"
              body={getSerialNo}
              style={{ width: "30px" }}
            />
            <Column field="source_value" sortable header={source_title} />
            {source === "priority" && (
              <Column
                field="source_value"
                sortable
                body={setTag}
                header="Tag"
              />
            )}
            {source === "lead-status" && (
              <Column
                body={(rowData) => tiggerTag(rowData, "book")}
                header="Tigger to Book"
              />
            )}
            {source === "lead-status" && (
              <Column
                body={(rowData) => tiggerTag(rowData, "finish")}
                header="Tigger to Finish"
              />
            )}
            {source === "lead-status" && (
              <Column
                body={(rowData) => tiggerTag(rowData, "decline")}
                header="Tigger to Decline"
              />
            )}
            <Column field="position" sortable header="Position" />
            <Column
              header="Action"
              body={actionBodyTemplate}
              exportable={false}
              style={{ width: "50px" }}
            />
          </DataTable>
        </div>
      )}

      {/* 2. add and edit data dialog */}
      <Sidebar
        visible={popup === 1}
        position="right"
        header={<h4 className="mt-3 mb-0">{source_title} Details</h4>}
        onHide={hideDialog}
        style={{ width: window.innerWidth < 768 ? "20rem" : "25rem" }}
        aria-label="Sidebar Details"
        closeOnEscape={false}
        dismissable={false}
      >
        <Divider />

        <div className="field mb-3">
          <div>
            <label htmlFor="source_value" className="font-bold mb-1">
              {source_title} *
            </label>
          </div>
          <InputText
            id="source_value"
            value={data.source_value}
            onChange={(e) => onInputChange(e, "source_value")}
            placeholder={`Please Enter ${source_title}`}
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.source_value,
            })}
          />
          <div>
            {errors.source_value && (
              <small className="p-error">{errors.source_value}</small>
            )}
          </div>
        </div>

        <div className="field mb-3">
          <div>
            <label htmlFor="position" className="font-bold mb-1">
              Position *
            </label>
          </div>
          <InputText
            id="position"
            value={data.position}
            onChange={(e) => onInputChange(e, "position")}
            placeholder={`Please Enter Position`}
            required
            style={{ width: "100%" }}
            className={classNames({
              "p-invalid": errors.position,
            })}
          />
          <div>
            {errors.position && (
              <small className="p-error">{errors.position}</small>
            )}
          </div>
        </div>
        {source === "priority" && (
          <>
            <div className="mb-2">Pick Colors</div>
            <div className="field mb-3 flex justify-content-start">
              <label htmlFor="color" className="font-bold me-2">
                Tag
              </label>
              <input
                type="color"
                name="color"
                id="color"
                className="me-3"
                value={data.color}
                onChange={(e) => onInputChange(e, "color")}
              />
              <label htmlFor="text" className=" me-2 font-bold">
                Text
              </label>
              <ToggleButton
                onLabel="Black"
                offLabel="White"
                id="text"
                onChange={(e) => onInputChange(e, "text")}
                checked={data.text === "black" ? true : false}
              />
            </div>
          </>
        )}
        {source === "lead-status" && (
          <>
            <div className="field mb-2">
              <Checkbox
                id="booking"
                inputId="booking"
                onChange={(e) => onInputChange(e, "booking")}
                checked={data.trigger_status === 1 ? true : false}
              />
              <label htmlFor="booking" className="ms-2 me-3">
                Tigger to book
              </label>
            </div>
            <div className="field mb-2">
              <Checkbox
                id="finish"
                inputId="finish"
                onChange={(e) => onInputChange(e, "finish")}
                checked={data.trigger_status === 2 ? true : false}
              />
              <label htmlFor="finish" className="ms-2 me-3">
                Tigger to finish
              </label>
            </div>
            <div className="field">
              <Checkbox
                id="decline"
                inputId="decline"
                name="decline"
                onChange={(e) => onInputChange(e, "decline")}
                checked={data.trigger_status === 3 ? true : false}
              />
              <label htmlFor="decline" className="ms-2">
                Tigger to decline
              </label>
            </div>
          </>
        )}
        <div className="field mt-5">{dataDialogFooter}</div>
      </Sidebar>

      {/* 2. delete single data dialog */}
      <Dialog
        visible={popup === 2 ? true : false}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteDialogFooter(deleteData)}
        onHide={() => setPopup(null)}
      >
        <div>
          <i
            className="pi pi-exclamation-triangle me-3"
            style={{ fontSize: "2rem" }}
          />
          {data && (
            <span>
              Are you sure you want to delete <b>{data.source_value}</b> ?
            </span>
          )}
        </div>
      </Dialog>

      {/* 3. delete many datas dialog */}
      <Dialog
        visible={popup === 3 ? true : false}
        style={{ width: "32rem" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        header="Confirm"
        modal
        footer={deleteDialogFooter(deleteSelectedDatas)}
        onHide={() => setPopup(null)}
      >
        <div>
          <i
            className="pi pi-exclamation-triangle me-3"
            style={{ fontSize: "2rem" }}
          />
          {data && (
            <span>Are you sure you want to delete the selected Items ?</span>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default AddonsList;
